/*
 * File: CustomMemberCard.tsx
 * Project: a-a
 * File Created: Friday, 20th December 2024 11:52:22 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Friday, 25th July 2025 2:38:47 am
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import React, { useState, useEffect } from 'react';
import CustomCard from '../../shared/CustomCard/CustomCard';
import { Box, Chip, Skeleton, Typography, useTheme } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import zeplinColor from '../../../theme/Color';
import formatCurrency from '../../../helper/short-currency';
import { LightTooltip } from '../../shared/CustomTooltip/CustomTooltip';

type Team = {
  id: string | null;
  position: string | null;
  name: string | null;
  mobile: string | null;
  phone: string | null;
  address: {
    city: string | null;
    country: string | null;
    geocodeAccuracy: string | null;
    latitude: string | null;
    longitude: string | null;
    postalCode: string | null;
    state: string | null;
    street: string | null;
  };
  company: string | null;
  email: string | null;
  openDeals: string | null;
  openTasks: string | null;
  title: string | null;
  profile_image_url?: string | null;
};

const CustomMemberCard = ({ member, loading }: { member: Team; loading: boolean }) => {
  const theme = useTheme();

  // State to manage truncated position and title
  const [truncatedPosition, setTruncatedPosition] = useState<string>('');
  const [truncatedTitle, setTruncatedTitle] = useState<string>('');

  // Function to truncate text based on screen size
  const truncateText = (text: string | null, maxLength: number) => {
    if (text && text.length > maxLength) {
      return text.slice(0, maxLength) + '...'; // Truncate with ellipsis
    }
    return text ?? '';
  };
  const screenWidth = window.innerWidth;
  useEffect(() => {
    // Dynamically set truncation based on screen size

    const positionMaxLength = member?.title
      ? screenWidth > 600
        ? 10
        : 5
      : screenWidth > 600
      ? 20
      : 15; // Adjust based on screen size
    const titleMaxLength = member?.position
      ? screenWidth > 600
        ? 10
        : 5
      : screenWidth > 600
      ? 20
      : 15; // Adjust based on screen size

    if (member?.position) {
      setTruncatedPosition(
        screenWidth > 1800 ? member.position : truncateText(member.position, positionMaxLength),
      );
    }
    if (member?.title) {
      setTruncatedTitle(
        screenWidth > 1800 ? member.title : truncateText(member.title, titleMaxLength),
      );
    }
  }, [member?.position, member?.title, screenWidth]);

  return (
    <>
      <CustomCard
        styles={{
          padding: '16px',
          gap: '12px',
          minWidth: '277px',
          // height: '261px',
          '&:hover': {
            // boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            borderColor: zeplinColor.BasicBlack,
          },
          borderRadius: '14px',
          border: `1px solid ${zeplinColor.Neutral5}`,
          display: 'flex',
          flexDirection: 'column',
          // overflow: 'hidden',
          cursor: 'pointer',
          width: '100%', // Ensures the card takes full width of the container
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          {' '}
          {member?.profile_image_url ? (
            <img
              src={member?.profile_image_url}
              alt='team-member-avatar'
              style={{
                position: 'relative',
                objectFit: 'fill',
                height: '82px',
                borderRadius: '14px 14px 14px 14px',
                border: `1px solid ${zeplinColor.Neutral3}`,
                // zIndex: 2,
                // margin: 'auto',
                // display: 'block',
                width: '82px',
                // padding: '4px',
              }}
            />
          ) : (
            <Box
              sx={{
                backgroundColor: loading ? zeplinColor.Neutral3 : zeplinColor.Brand1,
                borderRadius: '14px 14px 14px 14px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: '82px',
                maxWidth: '82px',
                height: '82px',

                // boxSizing: 'border-box',
              }}
              // style={{ width: '82px', height: '82px' }}
            >
              <Typography
                variant='display4'
                sx={{
                  color: zeplinColor.Neutral1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',

                  // margin: 0,
                  // padding: 0,
                  // height: '116px',
                }}
              >
                {member?.name
                  ?.split(' ')
                  .map((val, index) =>
                    index === 0 || index === (member?.name ?? '')?.split(' ').length - 1
                      ? val[0]
                      : '',
                  )}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  flex: 1,
                  // Optionally adjust other styles here
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  // overflow: 'hidden',
                }}
              >
                <LightTooltip
                  title={
                    <Typography variant='body3' color={zeplinColor.Neutral1}>
                      {member?.name || 'N/A'}
                    </Typography>
                  }
                  placement='bottom-start'
                  style={{
                    backgroundColor: zeplinColor.BasicBlack,
                    borderRadius: '8px',
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant='body1'
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      // textOverflow: 'ellipsis',
                      // width: '100%',
                      // display: 'block',
                      // maxWidth: { xs: '200px', xl: '175px' }, // Explicitly set max width to constrain text
                      maxWidth: 'calc(100% - 10px)',
                      textOverflow: 'ellipsis',
                      width: '100%',
                      // display: 'block',
                      boxSizing: 'border-box',
                    }}
                  >
                    {member?.name || 'N/A'}
                  </Typography>
                </LightTooltip>
              </Box>
            )}
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  flex: 1,

                  // Optionally adjust other styles here
                }}
              />
            ) : (
              member?.title && (
                <Box>
                  <Chip
                    label={
                      <Typography
                        variant='label1'
                        color={zeplinColor.Neutral1}
                        sx={{
                          // textAlign: 'center',
                          // display: 'flex',
                          // justifyContent: 'center',
                          // alignItems: 'center',
                          textAlign: 'center',
                          display: 'block', // Changed from flex to block for proper ellipsis
                          maxWidth: '100%', // Ensure it takes full width of container
                          whiteSpace: 'nowrap', // Keep text on one line
                          overflow: 'hidden', // Hide overflow
                          textOverflow: 'ellipsis', // Show ellipsis
                        }}
                      >
                        {member?.title || 'N/A'}
                      </Typography>
                    }
                    sx={{
                      // backgroundColor: zeplinColor.BasicBlack,
                      // padding: '5px 12px 5px 12px',
                      // borderRadius: '50px',
                      // // width: '50%',
                      // height: '24px',
                      backgroundColor: zeplinColor.BasicBlack,
                      padding: '5px 12px 5px 12px',
                      borderRadius: '50px',
                      height: '24px',
                      maxWidth: '100%', // Allow chip to take full width available
                      '& .MuiChip-label': {
                        // Target the inner label directly
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        maxWidth: '120px', // Adjust this value as needed
                      },
                    }}
                  />
                </Box>
              )
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: '12px' }}>
          <Box
            sx={{
              borderRadius: '8px',
              padding: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              backgroundColor: zeplinColor.Neutral3,
              flex: 1,
              textAlign: 'center',
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,

                  // Optionally adjust other styles here
                }}
              />
            ) : (
              <Typography variant={'body2'}>{member?.openTasks ?? 0}</Typography>
            )}
            <Typography variant={'label1'} color={zeplinColor.Neutral8}>
              Open Tasks
            </Typography>
          </Box>
          <Box
            sx={{
              borderRadius: '8px',
              padding: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              backgroundColor: zeplinColor.Neutral3,
              flex: 1,
              textAlign: 'center',
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,

                  // Optionally adjust other styles here
                }}
              />
            ) : (
              <Typography variant={'body2'}>
                ${formatCurrency(+(member?.openDeals || 0))}
              </Typography>
            )}
            <Typography variant={'label1'} color={zeplinColor.Neutral8}>
              Open Deals
            </Typography>
          </Box>
        </Box>
      </CustomCard>
    </>
  );
};

export default CustomMemberCard;
